import { useParams, useNavigate } from 'react-router-dom';
import {
  useTranslation,
  getOrderFulfillmentMethod,
  useAuthSession,
  OrderStatusTimeline,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useAdminOrderDetail } from '@/hooks/use-admin-order-detail';
import {
  OrderDetailSkeleton,
  OrderDetailNotFound,
  OrderDetailHeader,
  OrderDetailCustomer,
  OrderDetailAddress,
  OrderDetailNotes,
  OrderDetailLineItems,
  OrderDetailTotals,
  OrderDetailActions,
  OrderCourierAssignmentPanel,
  CourierProofReviewCard,
} from '@/features/orders';
import { useCourierPermissions } from '@/hooks/use-courier-permissions';
import { useStore } from '@/features/store-settings/use-store';

/**
 * F-022: Route orchestrator for `/orders/:orderId`.
 *
 * Extracts `orderId` from route params, fetches the full order detail, and
 * renders the appropriate state (loading skeleton, error/not-found, or the
 * two-column detail layout).
 *
 * Two-column grid at `lg+` (main content, sticky actions + timeline).
 * Single-column stacked on mobile.
 *
 * Sticky desktop column uses `lg:top-24` (96px = 64px topbar + 32px breathing room)
 * matching `product-detail-view.tsx`.
 */
export function OrderDetailRoute(): React.JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { dir } = useTranslation();

  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeQuery = useStore(storeId);
  const courierPermissions = useCourierPermissions(storeId);
  const { data, isLoading, isError, error, refetch } = useAdminOrderDetail(orderId);

  function handleBack(): void {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/orders');
    }
  }

  if (isLoading) {
    return <OrderDetailSkeleton />;
  }

  if (isError || !data) {
    return <OrderDetailNotFound error={error} onRetry={refetch} />;
  }

  return (
    <main dir={dir} data-testid="order-detail-route" className="bg-background min-h-screen">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <OrderDetailHeader order={data.order} onBack={handleBack} />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Main content */}
          <div className="flex min-w-0 flex-col gap-6">
            <OrderDetailCustomer order={data.order} />
            <OrderDetailAddress order={data.order} />
            <OrderDetailNotes notes={data.order.customer_notes} />
            <OrderDetailLineItems items={data.items} />
            <OrderDetailTotals order={data.order} />
          </div>

          {/* Sticky actions + timeline on desktop */}
          {/* top-24 = 96px = 64px topbar (sm:h-16) + 32px breathing room
              — matches product-detail-view.tsx */}
          <div className="flex min-w-0 flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
            <OrderDetailActions
              key={data.order.id}
              order={data.order}
              storeId={storeId ?? ''}
              courierAssignment={data.courier_assignment ?? null}
            />
            {storeId && data.order.delivery_method !== 'pickup' ? (
              <OrderCourierAssignmentPanel
                order={{
                  ...data.order,
                  courier_assignment: data.courier_assignment ?? null,
                }}
                storeId={storeId}
                storeName={storeQuery.data?.name ?? ''}
                canManageCouriers={courierPermissions.data?.canManageCouriers === true}
              />
            ) : null}
            {storeId &&
            data.order.delivery_method !== 'pickup' &&
            data.courier_assignment &&
            (data.courier_assignment.latest_proof_id ||
              data.order.status === 'shipped' ||
              data.order.status === 'delivered') ? (
              <CourierProofReviewCard
                storeId={storeId}
                assignment={data.courier_assignment}
                proof={data.courier_proof ?? null}
              />
            ) : null}
            <OrderStatusTimeline
              method={getOrderFulfillmentMethod(data.order)}
              status={data.order.status}
              shippedAt={data.order.shipped_at}
              deliveredAt={data.order.delivered_at}
              cancelledAt={data.order.cancelled_at}
              rejectedAt={data.order.rejected_at}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
